import { useState } from 'react';
import {React, Button, Collapse, Row, Col, useEffect } from '../utils/thirdpartyComponents';
import PlusIcon from '../assets/images/icons/plus';
import Addresspinicon from '../assets/images/address-pin-icon.svg';
import CWCheckbox from './CWCheckbox';
import CWInput from '../components/CWInput';
import CWSelect from '../components/CWSelect';
import { CompanyApi } from '../utils/apiUrls';
import axios from '../config/axiosConfig';
import { CITY_MAX_LENGTH } from '../utils/constants';
import { addressSchema } from '../validations/orderHardwareSchema';
import * as Yup from 'yup';
import { CountryItem, Option } from '../interfaces/CarWashInterface';
import { fetchStatesByCountry, fetchSupportedCountries, getCountryCodeFromRecord, getCountryDisplayName, getCountryFieldLabels, getPostalCodeMaxLength, getSelectedOptions, isAllowedPostalCodeInput, mapCountryOptions, normalizePostalCodeInput } from '../utils/countryState';
import { MessageToaster } from '../utils/toastUtil';

interface OrderAddAddressProps {
    onAddressAdd: (billingAddress: string, shippingAddress: string) => void;
   
}

interface AddressFormData {
    address: string;
    city: string;
    country: string;
    state: string;
    zipcode: string;
}

type AddressFormErrors = Partial<Record<keyof AddressFormData, string>>;

const getEmptyAddressForm = (country = ''): AddressFormData => ({
    address: '',
    city: '',
    country,
    state: '',
    zipcode: ''
});

const isAddressFormEmpty = (data: AddressFormData) =>
    !data.address.trim() &&
    !data.city.trim() &&
    !data.state.trim() &&
    !data.zipcode.trim();

const formatAddress = (data: AddressFormData) => {
    const countryName = getCountryDisplayName(data.country);
    return `${data.address}, ${data.city}, ${data.state}, ${data.zipcode}${countryName ? `, ${countryName}` : ''}`;
};

const parseAddressToFormData = (addressText: string, defaultCountry = ''): AddressFormData | null => {
    if (!addressText?.trim()) return null;

    const parts = addressText
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part !== '');

    if (parts.length < 4) return null;

    const toCountryCode = (value?: string) =>
        getCountryCodeFromRecord({ country: value }, { fallback: false }) || defaultCountry;

    if (parts.length >= 5) {
        const relevantParts = parts.slice(-5);
        return {
            address: relevantParts[0] || '',
            city: relevantParts[1] || '',
            state: relevantParts[2] || '',
            zipcode: relevantParts[3] || '',
            country: toCountryCode(relevantParts[4]),
        };
    }

    const relevantParts = parts.slice(-4);
    return {
        address: relevantParts[0] || '',
        city: relevantParts[1] || '',
        state: relevantParts[2] || '',
        zipcode: relevantParts[3] || '',
        country: defaultCountry,
    };
};

const OrderShippingAddress: React.FC<OrderAddAddressProps> = ({ onAddressAdd }) => {
    const [openBillingForm, setOpenBillingForm] = useState(false);
    const [openShippingForm, setOpenShippingForm] = useState(false);
    const [billingAddress, setBillingAddress] = useState("");
    const [shippingAddress, setShippingAddress] = useState("");
    const [sameAsBilling, setSameAsBilling] = useState(true);
    const [sameAsCarwashAddress, setSameAsCarwashAddress] = useState(true);
    
    const [defaultAddress,setDefaultAddress] = useState("");
    const [companyCountry, setCompanyCountry] = useState('');
    const [billingFormData, setBillingFormData] = useState<AddressFormData>(getEmptyAddressForm());
    const [shippingFormData, setShippingFormData] = useState<AddressFormData>(getEmptyAddressForm());
    const [billingErrors, setBillingErrors] = useState<AddressFormErrors>({});
    const [shippingErrors, setShippingErrors] = useState<AddressFormErrors>({});
    const [supportedCountries, setSupportedCountries] = useState<CountryItem[]>([]);
    const [stateOptions, setStateOptions] = useState<Option[]>([]);
    const toaster = new MessageToaster();
    const countryOptions = mapCountryOptions(supportedCountries);
    const selectedCountry = companyCountry || billingFormData.country;
    const { stateLabel, postalLabel } = getCountryFieldLabels(supportedCountries, selectedCountry);
    const postalMaxLength = getPostalCodeMaxLength(selectedCountry);


    
    useEffect(() => {
        getCountries();
        getCompany();
    }, []);

    useEffect(() => {
        if (sameAsBilling) {
            setShippingAddress(billingAddress);
        }
    }, [sameAsBilling, billingAddress]);

    useEffect(() => {
        onAddressAdd(billingAddress, shippingAddress);
    }, [billingAddress, shippingAddress]);

    const handleBillingInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const field = name as keyof AddressFormData;
        const nextValue = field === 'zipcode'
            ? normalizePostalCodeInput(value, selectedCountry)
            : value;

        if (field === 'zipcode' && !isAllowedPostalCodeInput(nextValue, selectedCountry)) {
            return;
        }

        if (billingErrors[field]) {
            setBillingErrors((prev) => ({
                ...prev,
                [field]: ""
            }));
        }

        setBillingFormData((prev) => ({ ...prev, [field]: nextValue }));
    };

    const handleShippingInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const field = name as keyof AddressFormData;
        const country = shippingFormData.country || selectedCountry;
        const nextValue = field === 'zipcode'
            ? normalizePostalCodeInput(value, country)
            : value;

        if (field === 'zipcode' && !isAllowedPostalCodeInput(nextValue, country)) {
            return;
        }

        if (shippingErrors[field]) {
            setShippingErrors((prev) => ({
                ...prev,
                [field]: ""
            }));
        }

        setShippingFormData((prev) => ({ ...prev, [field]: nextValue }));
    };

    const validateAddressForm = async (
        data: AddressFormData,
        setErrors: React.Dispatch<React.SetStateAction<AddressFormErrors>>,
        inputPrefix: 'billing' | 'shipping'
    ): Promise<boolean> => {
        try {
            await addressSchema.validate({
                ...data,
                country: data.country || companyCountry,
            }, { abortEarly: false });
            setErrors({});
            return true;
        } catch (error) {
            const newErrors: AddressFormErrors = {};

            if (error instanceof Yup.ValidationError) {
                error.inner.forEach((err) => {
                    if (!err.path) return;
                    newErrors[err.path as keyof AddressFormData] = err.message;
                });
            }

            setErrors(newErrors);

            const firstErrorField = Object.keys(newErrors)[0] as keyof AddressFormData | undefined;
            if (firstErrorField) {
                const element = document.getElementById(
                    `${inputPrefix}-${firstErrorField}`
                ) as HTMLInputElement | null;
                element?.focus();
            }

            return false;
        }
    };

    const saveBillingAddress = async () => {
        if (isAddressFormEmpty(billingFormData) && billingAddress.trim()) {
            setBillingErrors({});
            setOpenBillingForm(false);
            return;
        }

        const isValid = await validateAddressForm(billingFormData, setBillingErrors, 'billing');
        if (isValid) {
            const newAddress = formatAddress(billingFormData);
            setBillingAddress(newAddress);
            setOpenBillingForm(false);
            setBillingFormData(getEmptyAddressForm(companyCountry));
            setBillingErrors({});
        }
    };

    const saveShippingAddress = async () => {
        if (isAddressFormEmpty(shippingFormData) && shippingAddress.trim()) {
            setShippingErrors({});
            setOpenShippingForm(false);
            return;
        }

        const isValid = await validateAddressForm(shippingFormData, setShippingErrors, 'shipping');
        if (isValid) {
            const newAddress = formatAddress(shippingFormData);
            setShippingAddress(newAddress);
            setOpenShippingForm(false);
            setSameAsBilling(false);
            setShippingFormData(getEmptyAddressForm(companyCountry));
            setShippingErrors({});
        }
    };

    const handleBillingFormToggle = () => {
        const shouldOpen = !openBillingForm;
        setOpenBillingForm(shouldOpen);

        if (shouldOpen) {
            setBillingFormData(parseAddressToFormData(billingAddress, companyCountry) || getEmptyAddressForm(companyCountry));
            setBillingErrors({});
        }
    };

    const handleShippingFormToggle = () => {
        const shouldOpen = !openShippingForm;
        setOpenShippingForm(shouldOpen);

        if (shouldOpen) {
            setShippingFormData(parseAddressToFormData(shippingAddress, companyCountry) || getEmptyAddressForm(companyCountry));
            setShippingErrors({});
        }
    };

    const handleSameAsBilling = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setSameAsBilling(checked);

        if (checked) {
            setShippingAddress(billingAddress);
            setOpenShippingForm(false);
            setShippingFormData(getEmptyAddressForm(companyCountry));
            setShippingErrors({});
        }
        else{
            setShippingAddress('');
        }
    };

    const handlesameAsCarwashAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setSameAsCarwashAddress(checked);

        if (checked) {
            setBillingAddress(defaultAddress);
            setOpenBillingForm(false);
            setBillingFormData(getEmptyAddressForm(companyCountry));
            setBillingErrors({});
            if (sameAsBilling) setShippingAddress(defaultAddress);
        }
        else{
            setBillingAddress('');
            setOpenBillingForm(true);
            setBillingFormData(getEmptyAddressForm(companyCountry));
            setBillingErrors({});
            if (sameAsBilling) setShippingAddress('');
        }
    };

    const getCountries = async () => {
        try {
            const countries = await fetchSupportedCountries();
            setSupportedCountries(countries);
        } catch (error: any) {
            toaster.toastError(error.message);
        }
    };

    const getSateList = async (countryCode: string) => {
        if (!countryCode) {
            setStateOptions([]);
            return;
        }
        try {
            const options = await fetchStatesByCountry(countryCode);
            setStateOptions(options);
        } catch (error: any) {
            setStateOptions([]);
            toaster.toastError(error.message);
        }
    };

    const getCompany = async () => {
        try {
            const { data } = await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS);
            if (data?.error === 0) {
                const countryCode = getCountryCodeFromRecord(data.data, { fallback: false });
                setCompanyCountry(countryCode);
                setBillingFormData((prev) => ({ ...prev, country: countryCode }));
                setShippingFormData((prev) => ({ ...prev, country: countryCode }));
                if (countryCode) {
                    await getSateList(countryCode);
                }
                const countryName = getCountryDisplayName(countryCode);
                const newAddress = `${data.data.companyName}, ${data.data.address1}, ${data.data.city}, ${data.data.state}, ${data.data.zipCode}${countryName ? `, ${countryName}` : ''}`;
                setBillingAddress(newAddress);
                setShippingAddress(newAddress);
                setDefaultAddress(newAddress);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    };

    return (
        <div className='shipping-address-wrap mx-auto'>
            {/* Billing Address Section */}
            <div className="address-block">
                <div className="title d-flex align-items-center justify-content-between">
                    <h6 className='f-bold'>Billing Address</h6>
                    <CWCheckbox
                        id="sameAsCarwashAddress"
                        name="sameAsCarwashAddress"
                        label="Same as Car Wash Address"
                        checked={sameAsCarwashAddress}
                        onChange={handlesameAsCarwashAddress}
                    />
                </div>
                <div className="address-card d-flex align-items-center">
                    <img src={Addresspinicon} alt="Car Wash App" />
                    <p>{billingAddress}</p>
                    
                </div>
                
                <Button
                    variant="light"
                    className="light-btn add-btn ms-auto d-flex align-items-center"
                    onClick={handleBillingFormToggle}
                    aria-controls="billing-form-collapse"
                    aria-expanded={openBillingForm}
                    title='Add New Address'
                    disabled={sameAsCarwashAddress}
                >
                    Add New Address<PlusIcon />
                </Button>
                <Collapse in={openBillingForm}>
                    <div className="address-form mt-3 mb-3" id="billing-form-collapse">
                        {/* Input fields */}
                        <CWInput
                            id="billing-address"
                            name="address"
                            type="text"
                            label="Address"
                            value={billingFormData.address}
                            onChange={handleBillingInputChange}
                            errorMessage={billingErrors.address}
                        />
                        <CWInput
                            id="billing-city"
                            name="city"
                            type="text"
                            label="City"
                            maxLength={CITY_MAX_LENGTH}
                            value={billingFormData.city}
                            errorMessage={billingErrors.city}
                            onChange={handleBillingInputChange}
                        />
                        <CWSelect
                            id="billing-country"
                            name="country"
                            options={countryOptions}
                            values={getSelectedOptions(countryOptions, billingFormData.country || companyCountry)}
                            label="Country"
                            dropdownGap={0}
                            dropdownPosition="auto"
                            placeholder="Select"
                            multi={false}
                            searchable={false}
                            disabled
                            isMandatory
                            errorMessage={billingErrors.country}
                            className="no-input"
                        />
                        <Row>
                            <Col lg={6}>
                                <CWSelect
                                    id="billing-state"
                                    name="state"
                                    options={stateOptions}
                                    values={getSelectedOptions(stateOptions, billingFormData.state)}
                                    label={stateLabel}
                                    dropdownGap={0}
                                    dropdownPosition="auto"
                                    placeholder="Select"
                                    multi={false}
                                    searchable={true}
                                    isMandatory
                                    errorMessage={billingErrors.state}
                                    onChange={(selectedOptions) => handleBillingInputChange({
                                        target: { name: 'state', value: selectedOptions?.[0]?.value || '' }
                                    } as React.ChangeEvent<HTMLInputElement>)}
                                    className="no-input"
                                />
                            </Col>
                            <Col lg={6}>
                                <CWInput
                                    id="billing-zipcode"
                                    name="zipcode"
                                    type="text"
                                    label={postalLabel}
                                    isMandatory
                                    maxLength={postalMaxLength}
                                    value={billingFormData.zipcode}
                                    errorMessage={billingErrors.zipcode}
                                    onChange={handleBillingInputChange}
                                />
                            </Col>
                        </Row>
                        <Button
                            variant="light"
                            className="primary-btn ms-auto d-flex justify-content-center"
                            onClick={saveBillingAddress}
                            title='Save'
                        >
                            Save
                        </Button>
                    </div>
                </Collapse>
            </div>

            {/* Shipping Address Section */}
            <div className="address-block">
                <div className="title d-flex align-items-center justify-content-between">
                    <h6 className='f-bold'>Shipping Address</h6>
                    <CWCheckbox
                        id="billingaddress"
                        name="billingaddress"
                        label="Same as Billing Address"
                        checked={sameAsBilling}
                        onChange={handleSameAsBilling}
                    />
                </div>
                <div className="address-card d-flex align-items-center">
                    <img src={Addresspinicon} alt="Car Wash App" />
                    <p>{shippingAddress}</p>
                </div>
                <Button
                    variant="light"
                    className="light-btn add-btn ms-auto d-flex align-items-center"
                    onClick={handleShippingFormToggle}
                    aria-controls="shipping-form-collapse"
                    aria-expanded={openShippingForm}
                    id="addnewshipping"
                    disabled={sameAsBilling ? true : false}
                    title='Add New Address'
                >
                    Add New Address<PlusIcon />
                </Button>
                <Collapse in={openShippingForm}>
                    <div className="address-form mt-3 mb-3" id="shipping-form-collapse">
                        <CWInput
                            id="shipping-address"
                            name="address"
                            type="text"
                            label="Address"
                            value={shippingFormData.address}
                            onChange={handleShippingInputChange}
                            errorMessage={shippingErrors.address}
                        />
                        <CWInput
                            id="shipping-city"
                            name="city"
                            type="text"
                            label="City"
                            maxLength={CITY_MAX_LENGTH}
                            value={shippingFormData.city}
                            errorMessage={shippingErrors.city}
                            onChange={handleShippingInputChange}
                        />
                        <CWSelect
                            id="shipping-country"
                            name="country"
                            options={countryOptions}
                            values={getSelectedOptions(countryOptions, shippingFormData.country || companyCountry)}
                            label="Country"
                            dropdownGap={0}
                            dropdownPosition="auto"
                            placeholder="Select"
                            multi={false}
                            searchable={false}
                            disabled
                            isMandatory
                            errorMessage={shippingErrors.country}
                            className="no-input"
                        />
                        <Row>
                            <Col lg={6}>
                                <CWSelect
                                    id="shipping-state"
                                    name="state"
                                    options={stateOptions}
                                    values={getSelectedOptions(stateOptions, shippingFormData.state)}
                                    label={stateLabel}
                                    dropdownGap={0}
                                    dropdownPosition="auto"
                                    placeholder="Select"
                                    multi={false}
                                    searchable={true}
                                    isMandatory
                                    errorMessage={shippingErrors.state}
                                    onChange={(selectedOptions) => handleShippingInputChange({
                                        target: { name: 'state', value: selectedOptions?.[0]?.value || '' }
                                    } as React.ChangeEvent<HTMLInputElement>)}
                                    className="no-input"
                                />
                            </Col>
                            <Col lg={6}>
                                <CWInput
                                    id="shipping-zipcode"
                                    name="zipcode"
                                    type="text"
                                    label={postalLabel}
                                    isMandatory
                                    maxLength={postalMaxLength}
                                    value={shippingFormData.zipcode}
                                    errorMessage={shippingErrors.zipcode}
                                    onChange={handleShippingInputChange}
                                />
                            </Col>
                        </Row>
                        <Button
                            variant="light"
                            className="primary-btn ms-auto d-flex justify-content-center"
                            onClick={saveShippingAddress}
                            title='Save'
                        >
                            Save
                        </Button>
                    </div>
                </Collapse>
            </div>
        </div>
    );
};

export default OrderShippingAddress;
